import { component } from 'picoapp';
import Modal from 'utils/modal';
import debounce from 'lodash/debounce';
import getProductItemsFromClerkResponse from 'utils/get-product-items-from-clerk-response';
import { ClerkResponse } from '../../typescript/Clerk';

interface SearchResults {
  collections: string[]
  count?: number
  products: string[]
}

export default component(async (node) => {
  const closeBtn = node.querySelector('[data-close-btn]');
  const input = node.querySelector<HTMLInputElement>('[data-input]');
  const clearInputBtn = node.querySelector<HTMLElement>('[data-clear-input-btn]');
  const outputWrapper = node.querySelector('[data-output-wrapper]');

  const placeholderContent = outputWrapper.innerHTML;

  const searchStrings = theme.strings.search;

  let popularProducts: string[];
  let popularCollections: string[];

  let firstRender = true;

  const limit = 8;

  const renderPlaceholderContent = () => {
    outputWrapper.innerHTML = placeholderContent;
  };

  const renderResults = (results: SearchResults) => {
    /* eslint-disable @typescript-eslint/indent */
    outputWrapper.innerHTML = `<div class="live-search__output">
      <div class="live-search__collections">
        <p class="live-search__title">${(() => {
          let title: string;

          if (input.value !== '' || !popularCollections) {
            title = searchStrings.collections;
          } else {
            title = searchStrings.popularCollections;
          }

          return title;
        })()}</p>

        <ul class="live-search__collections__list">${(() => {
          let collectionResults: string;

          if (input.value !== '' && results.collections.length) {
            collectionResults = results.collections.join('');
          } else {
            collectionResults = popularCollections?.join('')
              || `<li class="mt-2 ml-2 font-light">${searchStrings.noResults.replace('%terms%', input.value)}</li>`;
          }

          return collectionResults;
        })()}</ul>
      </div>

      <div class="live-search__products">
        <p class="live-search__title">${(() => (input.value !== '' ? searchStrings.products : searchStrings.popularProducts))()}</p>
          <ul class="live-search__products__list">${(() => {
            let productResults: string;

            if (input.value !== '') {
              productResults = results.products.length
                ? results.products.join('')
                : `<li class="font-light">${searchStrings.noResults.replace('%terms%', input.value)}</li>`;
            } else {
              productResults = popularProducts.join('');
            }

            return productResults;
          })()}</ul>
      </div>
    </div>

    ${(() => (results.count > limit ? `<div class="mt-10 flex justify-center">
      <button type="submit" form="search" class="button button--dark">
        ${searchStrings.viewAll.replace('%count%', results.count.toString())}
      </button>
    </div>` : ''))()}`;
    /* eslint-enable @typescript-eslint/indent */

    Clerk('click', '*[data-clerk-product-id]');
  };

  const initPopular = async () => {
    const productResponse = await new Promise<ClerkResponse>((resolve) => {
      Clerk(
        'call',
        'recommendations/popular',
        {
          labels: ['Live search'],
          limit,
          attributes: ['handle'],
          facets: ['categories'],
        },
        async (clerkResponse: ClerkResponse) => {
          resolve(clerkResponse);
        },
        (clerkResponse: ClerkResponse) => {
          console.error(clerkResponse.message); // eslint-disable-line no-console
        },
      );
    });

    const products = await getProductItemsFromClerkResponse(productResponse, 'search-item');

    const productItems = products.map((html) => `<li class="live-search__products__item">${html}</li>`);

    const collectionQuery = productResponse.facets.categories?.slice(0, limit).map((category) => (
      category.name
    )).join(' ') || '';

    const collectionResponse = await new Promise<ClerkResponse>((resolve) => {
      Clerk(
        'call',
        'search/categories',
        {
          query: collectionQuery,
          limit,
        },
        (clerkResponse: ClerkResponse) => {
          resolve(clerkResponse);
        },
        (clerkResponse: ClerkResponse) => {
          console.error(clerkResponse.message); // eslint-disable-line no-console
        },
      );
    });

    const collections = collectionResponse.categories.slice(0, limit).map((category) => `<li class="live-search__collections__item">
        <a href="${category.url}" class="button button--sm button--light-border">${category.name}</a>
      </li>`);

    popularCollections = collections.length ? collections : undefined;
    popularProducts = productItems;

    renderResults({
      collections: popularCollections,
      products: popularProducts,
    });

    firstRender = false;
  };

  const initModal = () => {
    const modal = Modal(node);

    modal.on('open', () => {
      document.documentElement.style.overflow = 'hidden';
    });

    modal.on('opened', () => {
      input.focus();

      if (firstRender) {
        initPopular();
      }
    });

    modal.on('close', () => {
      input.value = '';
      input.dispatchEvent(new Event('change'));

      document.documentElement.style.overflow = '';
    });

    document.addEventListener('liveSearch:open', () => modal.open());
    closeBtn.addEventListener('click', () => modal.close());
  };

  const search = async () => {
    const getProductResponse = () => new Promise<ClerkResponse>((resolve) => {
      Clerk(
        'call',
        'search/predictive',
        {
          query: input.value,
          labels: ['Live search'],
          limit,
          attributes: ['handle'],
        },
        async (clerkResponse: ClerkResponse) => {
          resolve(clerkResponse);
        },
        (clerkResponse: ClerkResponse) => {
          console.error(clerkResponse.message); // eslint-disable-line no-console
        },
      );
    });

    const getCollections = () => new Promise<string[]>((resolve) => {
      Clerk(
        'call',
        'search/categories',
        {
          query: input.value,
          limit,
        },
        (clerkResponse: ClerkResponse) => {
          const collectionItems = clerkResponse.categories.map((category) => `<li class="live-search__collections__item">
              <a href="${category.url}" class="button button--sm button--light-border">${category.name}</a>
            </li>`);

          resolve(collectionItems);
        },
        (clerkResponse: ClerkResponse) => {
          console.error(clerkResponse.message); // eslint-disable-line no-console
        },
      );
    });

    renderPlaceholderContent();

    const [productResponse, collections] = await Promise.all([
      getProductResponse(),
      getCollections(),
    ]);

    const products = await getProductItemsFromClerkResponse(productResponse, 'search-item');

    const productItems = products.map((html) => `<li class="live-search__products__item">${html}</li>`);

    renderResults({
      collections,
      count: productResponse.hits,
      products: productItems,
    });
  };

  const initInput = () => { // eslint-disable-line consistent-return
    if (!Clerk) {
      return console.error('Clerk not found on window. Please check that Clerk is enabled in theme settings'); // eslint-disable-line no-console
    }

    input.addEventListener('keyup', () => {
      clearInputBtn.hidden = input.value === '';
    });

    input.addEventListener('keyup', debounce(search, 250));
  };

  const initClearInputBtn = () => {
    clearInputBtn.addEventListener('click', () => {
      input.value = '';
      input.dispatchEvent(new Event('keyup'));
      input.focus();
    });
  };

  const init = () => {
    initModal();
    initInput();
    initClearInputBtn();
  };

  init();
});
